import { useCallback, useEffect, useMemo } from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { connect, useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { Button } from 'UIPackage/MainModule/components/Button';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { makeNoLaterThenValidator } from 'CorePackage/MainModule/validators/isNoLaterThen';
import { makeNoSoonerThenValidator } from 'CorePackage/MainModule/validators/isNoSoonerThan';
import { IFederalLawSelectOption } from 'CorePackage/CatalogsModule/interfaces/IFederalLawSelectOption';
import {
  getCatalogKnmStatuses248SelectOptions,
  getCatalogKnmSubjectTypesSelectOptions,
  getCatalogKnmTypes248SelectOptionsSelector,
  getCatalogKnmTypes294SelectOptions,
  getCatalogKnmYearsSelectOptions,
  getFederalLaw248SelectOptions,
  getFederalLaw294SelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { IYearSelectOption } from 'CorePackage/MainModule/interfaces/IYearSelectOption';
import { catalogKnmYearsGet } from 'CorePackage/CatalogsModule/actions/catalogsActions';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IKnmStatusSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmStatusSelectOption';
import { getKnmErpStatusesSelectOptions } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmTypeSelectOption';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';

import { IKnmSearchInitFilter } from '../interfaces/IKnmSearchInitFilter';
import styles from './SearchKnmFiltersModal.module.scss';
import { knmSearchMonthOptions } from '../constants/knmSearchMonthOptions';
import {
  getKnmSearchFilter,
  getKnmSearchKnmSupervisionTypesErvkOptionsAll,
  getKnmSearchKnmSupervisionTypesOptionsAll,
} from '../selectors/knmSearchSelectors';
import {
  knmSearchGetData,
  knmSearchGetKnmSupervisionTypesErvkOptionsAll,
  knmSearchGetKnmSupervisionTypesOptionsAll,
  knmSearchSetFilter,
} from '../actions/knmSearchActions';
import { IKnmSubjectTypeSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmSubjectTypeSelectOption';
import { IDictionarySelectOption } from '../../../CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IKnmSubjectTypesERVK } from '../../../APIPackage/CatalogsModule/interfaces/IKnmSubjectTypesERVK';
import { IKnmSubjectTypeCommonSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmSubjectTypeCommonSelectOptions';
import { getDictionariesKnmSubjectTypesSelectOptions } from '../../../CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { IKnmSupervisionTypeSelectOption } from '../../../CorePackage/CatalogsModule/interfaces/IKnmSupervisionTypeSelectOption';

function validateForm(values: Partial<IKnmSearchInitFilter> | null) {
  const validateDatePicker = (fromDate: string | undefined, toDate: string | undefined, startDate?: boolean) => {
    if (startDate && toDate && fromDate) {
      return makeNoLaterThenValidator(
        'Дата начала периода должна быть меньше или равна дате окончания',
        () => toDate,
      )(fromDate);
    }

    if (fromDate && toDate) {
      return makeNoSoonerThenValidator(
        'Дата окончания периода должна быть больше или равна дате начала',
        () => fromDate,
      )(toDate);
    }

    if ((toDate && !fromDate && startDate) || (fromDate && !toDate && !startDate)) {
      return 'Поле не может быть пустым';
    }
  };

  return {
    fromDate: validateDatePicker(values?.fromDate, values?.toDate, true),
    toDate: validateDatePicker(values?.fromDate, values?.toDate),
  };
}

interface ISearchKnmFiltersModalProps {
  isVisible: boolean;
  onClose: () => void;
  filter: Partial<IKnmSearchInitFilter>;
  knmYearsSelectOptions: IYearSelectOption[];
  knmStatusesSelectOptions: IKnmStatusSelectOption[];
  federalLawsOptions: IFederalLawSelectOption[];
  knmTypesSelectOptions: IKnmTypeSelectOption[] | undefined;
  knmSupervisionTypesOptionsAll?: IKnmSupervisionTypeSelectOption[];
  knmSubjectTypesSelectOptions: IKnmSubjectTypeSelectOption[];
  knmSubjectTypesErvkSelectOptions: IDictionarySelectOption<IKnmSubjectTypesERVK>[] | undefined;
}

const SearchKnmFiltersModalWithoutState = ({
  isVisible,
  onClose,
  filter,
  knmYearsSelectOptions,
  knmStatusesSelectOptions,
  federalLawsOptions,
  knmTypesSelectOptions,
  knmSupervisionTypesOptionsAll,
  knmSubjectTypesSelectOptions,
  knmSubjectTypesErvkSelectOptions,
}: ISearchKnmFiltersModalProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  useEffect(() => {
    dispatch(catalogKnmYearsGet());
    dispatch(knmSearchGetKnmSupervisionTypesOptionsAll());
    dispatch(knmSearchGetKnmSupervisionTypesErvkOptionsAll());
  }, [dispatch]);

  const handleFiltersFormSubmit = useCallback(
    (values: Partial<IKnmSearchInitFilter> | null) => {
      dispatch(knmSearchSetFilter(values ?? {}));
      dispatch(knmSearchGetData());
      onClose();
    },
    [dispatch, onClose],
  );

  const knmSupervisionTypesOptionsAllWithCodes = useMemo(() => {
    if (knmSupervisionTypesOptionsAll?.length) {
      return knmSupervisionTypesOptionsAll.map(sup => {
        const labelWithDigitCode = sup.digitCode ? sup.digitCode?.toString().concat(` ${sup.label}`) : null;
        const labelWithCode = sup.code ? sup.code?.toString().concat(` ${sup.label}`) : null;

        return { ...sup, label: labelWithDigitCode || (labelWithCode ?? sup.label) };
      });
    }

    return undefined;
  }, [knmSupervisionTypesOptionsAll]);

  const knmSubjectTypesCommonSelectOptions = useMemo<IKnmSubjectTypeCommonSelectOption[]>(
    () => [...mapSubjectTypes(knmSubjectTypesSelectOptions), ...mapErvkSubjectTypes(knmSubjectTypesErvkSelectOptions)],
    [knmSubjectTypesSelectOptions, knmSubjectTypesErvkSelectOptions],
  );

  function mapSubjectTypes(items: IKnmSubjectTypeSelectOption[] | undefined): IKnmSubjectTypeCommonSelectOption[] {
    if (!Array.isArray(items)) {
      return [];
    }

    return items.map(item => ({
      id: item.id,
      name: item.name,
      code: item.code,
      title: item.name,
      label: item.label,
      value: item.code,
    }));
  }

  function mapErvkSubjectTypes(
    items: IDictionarySelectOption<IKnmSubjectTypesERVK>[] | undefined,
  ): IKnmSubjectTypeCommonSelectOption[] {
    if (!Array.isArray(items)) {
      return [];
    }

    return items
      .filter(item => item.code !== null)
      .map(item => ({
        id: item.id,
        name: item.title,
        code: item.code ?? '',
        title: item.title,
        label: item.label,
        value: item.code ?? '',
      }));
  }

  const renderFiltersForm = useCallback(
    ({ handleSubmit }: FormRenderProps<Partial<IKnmSearchInitFilter> | null>) => (
      <form onSubmit={handleSubmit}>
        <ModalHeader title="Фильтры" color="black" />
        <ModalBody overflow="scroll" border="bottom">
          <Row>
            <FieldBlock label="Федеральный закон" labelFontSize="small">
              <SelectInputField name="federalLaws" options={federalLawsOptions} isMulti />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Наименование органа контроля (надзора)" labelFontSize="small">
              <TextInputField name="frguOrgName" />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Наименование органа прокуратуры" labelFontSize="small">
              <TextInputField name="prosecutorName" />
            </FieldBlock>
          </Row>
          <Row notWrap>
            <FieldBlock label="Тип субъекта КНМ" labelFontSize="small" size="col-3">
              <SelectInputField
                name="subjectTypeCodes"
                options={knmSubjectTypesCommonSelectOptions}
                className="shared-field"
                isMulti
              />
            </FieldBlock>
            <FieldBlock label="Характер КНМ" labelFontSize="small" size="col-3">
              <SelectInputField name="knmType" options={knmTypesSelectOptions} className="shared-field" />
            </FieldBlock>
            <FieldBlock label="Год проведения КНМ" labelFontSize="small" size="col-3">
              <SelectInputField name="year" options={knmYearsSelectOptions} className="shared-field" isMulti />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Вид государственного надзора" labelFontSize="small">
              <SelectInputField name="supervisionName" options={knmSupervisionTypesOptionsAllWithCodes} isSearchable />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Наименование проверяемого лица" labelFontSize="small">
              <TextInputField name="organizationName" />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Период проведения" size="half" labelFontSize="small">
              <Row notWrap notSpace margin="none">
                <DatePickerField opensOnTop name="fromDate" className="shared-field" />
                <div className={styles.Divider}>-</div>
                <DatePickerField opensOnTop name="toDate" className="shared-field" />
              </Row>
            </FieldBlock>
            <FieldBlock label="Месяц даты начала проведения КНМ" labelFontSize="small" size="half">
              <SelectInputField name="startMonth" options={knmSearchMonthOptions} className="shared-field" />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Период проведения" size="col-3" labelFontSize="small">
              <TextInputField name="erpId" className="shared-field" />
            </FieldBlock>
            <FieldBlock label="ИНН" size="col-3" labelFontSize="small">
              <TextInputField name="inn" className="shared-field" />
            </FieldBlock>
            <FieldBlock label="ОГРН" size="col-3" labelFontSize="small">
              <TextInputField name="ogrn" className="shared-field" />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Статус КНМ" labelFontSize="small">
              <SelectInputField opensOnTop name="status" options={knmStatusesSelectOptions} isMulti />
            </FieldBlock>
          </Row>
        </ModalBody>
        <ModalActions placement="left">
          <Button type="submit" color="primary" size="large" text="Применить" />
        </ModalActions>
      </form>
    ),
    [
      federalLawsOptions,
      knmStatusesSelectOptions,
      knmSubjectTypesSelectOptions,
      knmSupervisionTypesOptionsAllWithCodes,
      knmTypesSelectOptions,
      knmYearsSelectOptions,
    ],
  );

  return (
    <Modal visible={isVisible} onClose={onClose} modalBodyClassName={styles.Modal} overflowBody="unset">
      <Form
        initialValues={filter}
        render={renderFiltersForm}
        onSubmit={handleFiltersFormSubmit}
        validate={validateForm}
      />
    </Modal>
  );
};

const mapStateToProps = (state: IRootState) => ({
  filter: getKnmSearchFilter(state),
  federalLawsOptions:
    getLawInterface(state) === LawInterfacesEnum.FZ_248
      ? getFederalLaw248SelectOptions(state)
      : getFederalLaw294SelectOptions(state),
  knmYearsSelectOptions: getCatalogKnmYearsSelectOptions(state),
  knmTypesSelectOptions:
    getLawInterface(state) === LawInterfacesEnum.FZ_248
      ? getCatalogKnmTypes248SelectOptionsSelector(state)({})
      : getCatalogKnmTypes294SelectOptions(state),
  knmStatusesSelectOptions:
    getLawInterface(state) === LawInterfacesEnum.FZ_248
      ? getCatalogKnmStatuses248SelectOptions(state)
      : getKnmErpStatusesSelectOptions(state),
  knmSubjectTypesSelectOptions: getCatalogKnmSubjectTypesSelectOptions(state),
  knmSupervisionTypesOptionsAll:
    getLawInterface(state) === LawInterfacesEnum.FZ_248
      ? getKnmSearchKnmSupervisionTypesErvkOptionsAll(state)
      : getKnmSearchKnmSupervisionTypesOptionsAll(state),
  knmSubjectTypesErvkSelectOptions: getDictionariesKnmSubjectTypesSelectOptions(state),
});

export const SearchKnmFiltersModal = connect(mapStateToProps)(SearchKnmFiltersModalWithoutState);
